// we have to use HOCS with Apollo 1 version

import { graphql } from 'react-apollo';
import {
  assignmentPlayerData,
  startAssignmentMutation,
  assignmentSubmitMutation,
  assessmentPreviewData,
  assignmentSession,
  assignmentTimer,
  assignmentReview,
  assignmentQuestionData,
  getAssessmentInfo,
  worldLanguagePilotPlayerData,
} from 'legacy-src/queries';
import { contructParamsForLDBImpersonating } from 'legacy/src/components/impersonating/utils';
import addLearnosityPerformance from 'chameleon-src/utility/addLearnosityPerformance';

const { stringify } = JSON;

export const withStartAssignment = graphql(startAssignmentMutation, {
  props: ({ mutate }) => ({
    startAssignment: (assignmentId, isImpersonating) =>
      mutate({
        variables: {
          assignmentId: Number.parseInt(assignmentId, 10),
          isImpersonating,
        },
      }),
  }),
});

export const withAssignmentSubmit = graphql(assignmentSubmitMutation, {
  props: ({ mutate }) => ({
    submitAssignment: (assignmentId) =>
      mutate({
        variables: {
          assignmentId,
        },
      }),
  }),
});

export const getAssignmentPlayerData = (
  params,
  learnosityConfig,
  cookies,
  isImpersonating,
) => {
  let impersonatingDetails = {};
  if (isImpersonating) {
    impersonatingDetails = contructParamsForLDBImpersonating();
  }

  return {
    query: assignmentPlayerData,
    fetchPolicy: 'network-only',
    variables: {
      assignmentId: params.assignmentId,
      subjectId: params.subject,
      config: stringify(learnosityConfig),
      ldbChallenge: cookies && cookies.get('rldbrv'),
      isImpersonating,
      impersonatingDetails: stringify(impersonatingDetails),
    },
  };
};

export const withAssessmentPreview = graphql(assessmentPreviewData, {
  options: (props) => {
    const {
      itemId,
      learnosityConfig,
      userData,
      subjectInitId,
      params: { subject },
    } = props;
    return {
      variables: {
        assessmentId: itemId,
        config: JSON.stringify(addLearnosityPerformance(learnosityConfig)),
        educationPeriod: userData.chosenEducationPeriodCode,
        subjectId: parseInt(subject || subjectInitId, 10),
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    };
  },
  props: ({ data }) => {
    const { loading, assessment, assessmentPreview, refetch } = data;
    if (assessment && assessmentPreview) {
      const {
        learnositySignedRequest,
        allowCalculatorForItems,
        sppVideos = {},
      } = assessmentPreview;
      return {
        loading,
        assessment,
        refetch,
        questions: assessment.questions,
        learnosityServerData: JSON.parse(learnositySignedRequest),
        allowCalculatorForItems,
        sppVideos,
      };
    }
  },
});

export const withTimer = graphql(assignmentTimer, {
  options: ({ params, isImpersonating = false }) => ({
    variables: {
      assignmentId: params.assignmentId,
      isImpersonating,
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data: { loading, assignment: assignmentTimerData } }) => {
    return {
      isAssignmentTimerLoading: loading,
      assignmentTimerData: assignmentTimerData || {},
    };
  },
});

export const withSession = graphql(assignmentSession, {
  options: ({ params, isImpersonating = false }) => ({
    variables: {
      assignmentId: Number.parseInt(params.assignmentId, 10),
      isImpersonating,
    },
    fetchPolicy: 'network-only',
  }),
  props: ({
    data: { loading, assignmentSession: assignmentSessionData, error, refetch },
  }) => {
    let props = {
      isAssignmentSessionLoading: loading,
      assignmentError: {
        hasError: !!error,
        message: (error || {}).message,
      },
    };

    if (!loading && !error && assignmentSessionData) {
      const { timedSession } = assignmentSessionData;
      props.checkTimeExtension = refetch;

      if (timedSession) {
        props.timerInfo = timedSession;
      }
    }

    return props;
  },
});

export const withAssignmentReview = graphql(assignmentReview, {
  options: (props) => {
    const {
      learnosityConfig,
      userData,
      params: { subject, assignmentId, studentId },
    } = props;
    return {
      variables: {
        assignmentId,
        /**
         * studentId is available when the teacher is looking at the student's work
         * otherwise if studentId is not available, then it's a student looking at
         * their own work so we use their initId
         */

        studentId: studentId || userData.me.initId,
        config: JSON.stringify(addLearnosityPerformance(learnosityConfig)),
        inline: true,
        assignmentIdAsInt: Number(assignmentId),
        subjectId: Number(subject),
        showCorrectAnswers: true,
        scorerType: 'teacher',
      },
      fetchPolicy: 'network-only',
    };
  },

  props: ({ data }) => {
    const {
      assignmentReview: assignmentReviewData,
      assignment,
      error,
      loading,
    } = data || {};
    if (loading) {
      return {
        assignmentReviewLoading: loading,
      };
    }

    if (assignmentReviewData) {
      const { assessment } = assignment;
      const {
        learnositySignedRequest,
        allowCalculatorForItems,
        status,
        scoreDisplay,
        sppVideos,
      } = assignmentReviewData;
      return {
        learnosityServerData: JSON.parse(learnositySignedRequest),
        allowCalculatorForItems,
        assignmentStatus: status,
        scoreDisplay,
        sppVideos,
        assessment,
        assignmentStudents: assignment?.students || [],
      };
    }

    if (error) {
      return {
        assignmentReviewError: error.message,
      };
    }

    return {
      assignmentReviewError: 'No assignment data available.',
    };
  },
});

export const withAssignmentQuestionData = graphql(assignmentQuestionData, {
  options: (props) => ({
    variables: {
      assignmentId: props.assignmentId || props.params.assignmentId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  }),
  props: ({ data }) => {
    const { assignment, loading } = data;

    if (assignment) {
      return {
        assignment,
        assignmentLoading: loading,
      };
    }
  },
});

export const withAssessmentInfo = graphql(getAssessmentInfo, {
  options: ({ previewId, chosenEducationPeriodCode }) => ({
    variables: {
      assessmentId: previewId,
      educationPeriod: chosenEducationPeriodCode,
    },
    // this request can be cached
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => {
    const { loading, assessment } = data;
    if (assessment) {
      return {
        loadingCurrentItem: loading,
        currentItem: assessment,
      };
    }
  },
});

export const getWorldLanguagePilotPlayerData = graphql(
  worldLanguagePilotPlayerData,
  {
    options: ({ params, isImpersonating, isWorldLanguageApReader }) => ({
      variables: {
        isImpersonating,
        isWorldLanguageApReader,
        activityId: params.activityId,
        subjectId: params.subject,
        studentId: params.studentId,
      },
      fetchPolicy: 'network-only',
    }),
    props: ({ data }) => {
      const { loading, worldLanguagePilotPlayer } = data;

      if (loading) {
        return {
          assignmentPlayerLoading: loading,
        };
      }

      return {
        assignmentPlayerLoading: false,
        worldLanguagePilotPlayer,
      };
    },
  },
);
